import { sanitizeHtml } from '../utils/sanitizeHtml';
import { jsPDF } from 'jspdf';
import CryptoJS from 'crypto-js';
import { getSharedNdaTemplate } from './firebase/ndaTemplateService';

export class PDFService {
  // Helper function to ensure checkbox character is properly handled
  // jsPDF may not render Unicode checkbox correctly, so we use a compatible character
  static getCheckboxSymbol() {
    // Try Unicode checkbox first, but have fallback
    // If jsPDF doesn't support it, the character will be replaced
    return '☑';
  }
  
  // Replace & with checkbox, handling all edge cases
  static fixAuthorizationCheckbox(content) {
    if (!content || typeof content !== 'string') return content;
    
    if (content.includes('I authorize') && content.includes('Builders Breakthrough Advisors')) {
      const checkboxSymbol = PDFService.getCheckboxSymbol();
      // Replace & (with any whitespace) before "I authorize" - most aggressive pattern
      content = content.replace(/&(amp;)?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, checkboxSymbol + ' I authorize');
      // Also handle at start of line or after whitespace
      content = content.replace(/(^|[\r\n\s]+)&+(amp;)?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, '$1' + checkboxSymbol + ' I authorize');
    }
    
    return content;
  }
  
  static async generateNDAPDF(ndaDocument) {
    const doc = new jsPDF();
    
    // Use finalizedDocumentContent if available (for fully executed NDAs), otherwise fall back to documentContent
    const documentContent = ndaDocument.finalizedDocumentContent || ndaDocument.documentContent || this.generateFullNDAContent(ndaDocument);
    
    // Try to load template elements if not already available
    let templateElements = ndaDocument.templateElements;
    
    // Check if template elements are nested in templateData
    if (!templateElements && ndaDocument.templateData && ndaDocument.templateData.ndaElements) {
      templateElements = ndaDocument.templateData.ndaElements;
    }
    
    // Check if template elements are nested in template
    if (!templateElements && ndaDocument.template && ndaDocument.template.ndaElements) {
      templateElements = ndaDocument.template.ndaElements;
    }
    
    // If still no template elements, try to load from shared template
    if (!templateElements) {
      try {
        const template = await getSharedNdaTemplate();
        templateElements = template.ndaElements;
      } catch (error) {
        console.warn('Could not load NDA template elements:', error);
        templateElements = null;
      }
    }
    
    // Create enhanced NDA document with template elements
    const enhancedNDADocument = {
      ...ndaDocument,
      templateElements: templateElements
    };
    
    // Determine if this is a contract or NDA based on document type or content
    const isContract = ndaDocument.templateElements && !ndaDocument.templateData?.ndaElements;
    const docType = isContract ? 'CONTRACT' : 'NDA';
    
    console.log(`Enhanced ${docType} document for PDF generation:`, {
      hasTemplateElements: !!templateElements,
      templateElementsCount: templateElements ? templateElements.length : 0,
      hasTemplateData: !!ndaDocument.templateData,
      hasFinalizedContent: !!ndaDocument.finalizedDocumentContent,
      hasDocumentContent: !!documentContent
    });
    
    // Debug: Log page break information
    if (templateElements && templateElements.length > 0) {
      const elementsWithPageBreaks = templateElements.filter(el => el.hasPageBreak);
      console.log(`${docType} - Elements with page breaks found:`, elementsWithPageBreaks.length);
      console.log(`${docType} - Page break elements:`, elementsWithPageBreaks.map(el => ({
        id: el.id,
        type: el.type,
        content: el.content?.substring(0, 50),
        hasPageBreak: el.hasPageBreak
      })));
      
      // Also log all elements to verify hasPageBreak is present
      console.log(`${docType} - All template elements (first 5):`, templateElements.slice(0, 5).map(el => ({
        id: el.id,
        type: el.type,
        hasPageBreak: el.hasPageBreak,
        hasPageBreakType: typeof el.hasPageBreak
      })));
    }
    
    // Generate PDF with document content only, respecting page breaks
    this.addDocumentContentToPDF(doc, documentContent, enhancedNDADocument);
    
    // Add full audit details page after the last page break
    this.addAuditDetailsPage(doc, enhancedNDADocument);
    
    // Add header (logo) and footer (page numbers) to all pages
    await this.addHeaderAndFooter(doc);
    
    // Calculate hash of the full document content
    const hash = CryptoJS.SHA256(documentContent).toString();
    
    return { pdf: doc, hash: hash, content: documentContent };
  }

  static addDocumentContentToPDF(doc, documentContent, ndaDocument) {
    // PRIORITY 1: Use finalizedDocumentContent if available (has all variables already replaced)
    // This ensures the downloaded PDF matches what's shown in the popup
    if (ndaDocument.finalizedDocumentContent) {
      console.log('Using finalized document content for PDF (all variables already replaced)');
      this.addFinalizedDocumentContent(doc, ndaDocument.finalizedDocumentContent);
      return;
    }
    
    // PRIORITY 2: Use template elements for proper styling and page breaks (only if no finalized content)
    // Extract template elements from various possible sources
    let templateElements = ndaDocument.templateElements;
    
    // Check if template elements are nested in templateData
    if (!templateElements && ndaDocument.templateData && ndaDocument.templateData.ndaElements) {
      templateElements = ndaDocument.templateData.ndaElements;
    }
    
    // Check if template elements are nested in template
    if (!templateElements && ndaDocument.template && ndaDocument.template.ndaElements) {
      templateElements = ndaDocument.template.ndaElements;
    }
    
    if (templateElements && Array.isArray(templateElements) && templateElements.length > 0) {
      // Use template elements for proper styling and page breaks
      console.log('Using template elements for PDF generation:', templateElements.length, 'elements found');
      
      // Check for page breaks in template elements
      const elementsWithPageBreaks = templateElements.filter(el => el.hasPageBreak);
      console.log('Elements with page breaks:', elementsWithPageBreaks.length, elementsWithPageBreaks.map(el => ({ id: el.id, type: el.type, content: el.content?.substring(0, 50) })));
      
      const enhancedNDADocument = {
        ...ndaDocument,
        templateElements: templateElements
      };
      this.addDocumentContentWithPageBreaks(doc, enhancedNDADocument);
    } else {
      // Final fallback to simple text rendering
      console.log('No template elements or finalized content found, using simple text rendering');
      this.addSimpleDocumentContent(doc, documentContent);
    }
  }

  static addDocumentContentWithPageBreaks(doc, ndaDocument) {
    // Start content below logo (logo is 10mm from top, 20mm tall, plus 10mm spacing = 40mm)
    let yPosition = 40;
    let isFirstElement = true;
    
    // Determine if this is a contract or NDA
    const isContract = ndaDocument.templateElements && !ndaDocument.templateData?.ndaElements;
    const docType = isContract ? 'CONTRACT' : 'NDA';
    
    console.log(`=== ${docType} PAGE BREAK DEBUG ===`);
    console.log(`${docType} - Template elements count:`, ndaDocument.templateElements?.length || 0);
    console.log(`${docType} - Template elements:`, ndaDocument.templateElements?.map(el => ({
      id: el.id,
      type: el.type,
      hasPageBreak: el.hasPageBreak,
      hasPageBreakType: typeof el.hasPageBreak,
      content: el.content?.substring(0, 50)
    })));
    
    // Process each template element
    for (const element of ndaDocument.templateElements) {
      console.log(`${docType} - Processing element ${element.id}: hasPageBreak=${element.hasPageBreak} (type: ${typeof element.hasPageBreak}), isFirstElement=${isFirstElement}`);
      
      // Add page break if this element has one and it's not the first element
      // Check for both boolean true and truthy values
      const shouldAddPageBreak = (element.hasPageBreak === true || element.hasPageBreak === 'true') && !isFirstElement;
      if (shouldAddPageBreak) {
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        console.log(`${docType} - Adding page break for element:`, element.id, element.type, element.content?.substring(0, 50));
        console.log(`${docType} - Current page before break:`, currentPage);
        doc.addPage();
        const newPage = doc.internal.getCurrentPageInfo().pageNumber;
        console.log(`${docType} - New page after break:`, newPage);
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
      }
      
      // Check if element will fit on current page, add page break if needed
      // BUT only if the element doesn't already have a page break flag
      // Also skip if we just added a manual page break above
      const estimatedHeight = this.estimateElementHeight(element);
      if (yPosition + estimatedHeight > 270 && !isFirstElement && !element.hasPageBreak && !shouldAddPageBreak) {
        console.log(`${docType} - Adding automatic page break for element:`, element.id, element.type);
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
      }
      
      // Render the element with proper styling, passing the hasPageBreak flag
      // Pass true for hasPageBreak if we just added a page break, to prevent double page breaks in rendering
      const result = this.renderStyledElement(doc, element, ndaDocument, yPosition, shouldAddPageBreak || element.hasPageBreak);
      yPosition = result.yPosition;
      
      isFirstElement = false;
    }
  }

  static addFinalizedDocumentContent(doc, documentContent) {
    // Parse HTML content and render with proper formatting
    this.renderHtmlContent(doc, documentContent);
  }

  static renderHtmlContent(doc, htmlContent) {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitizeHtml(htmlContent);
    
    // Start content below logo (logo is 10mm from top, 20mm tall, plus 10mm spacing = 40mm)
    let yPosition = 40;
    const baseFontSize = this.convertCssSizeToPdf('14px');
    let signaturesPageAdded = false; // Track if we've added the signatures page
    
    // Process each child element
    const processElement = (element, indent = 0) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
      }
      
      const tagName = element.tagName ? element.tagName.toLowerCase() : '';
      const textContent = element.textContent || '';
      
      // Check for signatures section - add page break and header before "Builders Breakthrough Advisors By:"
      if (!signaturesPageAdded && (
        textContent.includes('Builders Breakthrough Advisors By:') ||
        textContent.includes('IN WITNESS WHEREOF') ||
        (textContent.includes('Builders Breakthrough Advisors') && textContent.includes('By:'))
      )) {
        // Add page break before signatures section
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
        
        // Add "Signatures Page" header
        const headerFontSize = this.convertCssSizeToPdf('24px');
        doc.setFontSize(headerFontSize);
        doc.setFont('helvetica', 'bold');
        doc.text('Signatures Page', 15, yPosition);
        yPosition += headerFontSize * 0.8;
        yPosition += this.convertCssSizeToPdf('16px'); // Extra spacing after header
        
        signaturesPageAdded = true;
      }
      
      // Check for page break indicators in the content
      if (textContent.includes('— Page Break —') || textContent.includes('Page Break')) {
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
        return;
      }
      
      // Handle different HTML elements
      switch (tagName) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          // Check if this header contains the signatures section marker
          if (!signaturesPageAdded && (
            textContent.includes('Builders Breakthrough Advisors By:') ||
            textContent.includes('IN WITNESS WHEREOF') ||
            (textContent.includes('Builders Breakthrough Advisors') && textContent.includes('By:'))
          )) {
            // Add page break before signatures section
            doc.addPage();
            yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
            
            // Add "Signatures Page" header
            const signaturesHeaderFontSize = this.convertCssSizeToPdf('24px');
            doc.setFontSize(signaturesHeaderFontSize);
            doc.setFont('helvetica', 'bold');
            doc.text('Signatures Page', 15, yPosition);
            yPosition += signaturesHeaderFontSize * 0.8;
            yPosition += this.convertCssSizeToPdf('16px'); // Extra spacing after header
            
            signaturesPageAdded = true;
          }
          
          // Header styling
          const headerFontSize = this.convertCssSizeToPdf(tagName === 'h1' ? '24px' : tagName === 'h2' ? '20px' : '18px');
          doc.setFontSize(headerFontSize);
          doc.setFont('helvetica', 'bold');
          
          // Add spacing before header
          yPosition += this.convertCssSizeToPdf('12px');
          
          const headerLines = doc.splitTextToSize(textContent, 165);
          for (const line of headerLines) {
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
            }
            doc.text(line, 15, yPosition);
            yPosition += headerFontSize * 0.6;
          }
          
          // Add spacing after header
          yPosition += this.convertCssSizeToPdf('8px');
          break;
          
        case 'p':
          // Check if this paragraph contains the signatures section marker
          if (!signaturesPageAdded && (
            textContent.includes('Builders Breakthrough Advisors By:') ||
            textContent.includes('IN WITNESS WHEREOF') ||
            (textContent.includes('Builders Breakthrough Advisors') && textContent.includes('By:'))
          )) {
            // Add page break before signatures section
            doc.addPage();
            yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
            
            // Add "Signatures Page" header
            const headerFontSize = this.convertCssSizeToPdf('24px');
            doc.setFontSize(headerFontSize);
            doc.setFont('helvetica', 'bold');
            doc.text('Signatures Page', 15, yPosition);
            yPosition += headerFontSize * 0.8;
            yPosition += this.convertCssSizeToPdf('16px'); // Extra spacing after header
            
            signaturesPageAdded = true;
          }
          
          // Paragraph styling
          doc.setFontSize(baseFontSize);
          
          // Check if this paragraph contains a signature line
          const isSignatureParagraph = /Signature:\s*[^\n]+/i.test(textContent);
          
          // Add spacing before paragraph
          yPosition += this.convertCssSizeToPdf('6px');
          
          // For authorization text, use narrower width
          const isAuthorizationText = textContent.includes('authorize') && textContent.includes('debit');
          const textWidth = isAuthorizationText ? 150 : 165;
          const paragraphLines = doc.splitTextToSize(textContent, textWidth);
          for (const line of paragraphLines) {
            // Check each line for signatures section marker (in case it's split across lines)
            if (!signaturesPageAdded && (
              line.includes('Builders Breakthrough Advisors By:') ||
              line.includes('IN WITNESS WHEREOF') ||
              (line.includes('Builders Breakthrough Advisors') && line.includes('By:'))
            )) {
              // Add page break before signatures section
              doc.addPage();
              yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
              
              // Add "Signatures Page" header
              const headerFontSize = this.convertCssSizeToPdf('24px');
              doc.setFontSize(headerFontSize);
              doc.setFont('helvetica', 'bold');
              doc.text('Signatures Page', 15, yPosition);
              yPosition += headerFontSize * 0.8;
              yPosition += this.convertCssSizeToPdf('16px'); // Extra spacing after header
              
              signaturesPageAdded = true;
            }
            
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
            }
            
            // Check if this specific line contains signature text
            const lineIsSignature = /Signature:\s*[^\n]+/i.test(line);
            if (lineIsSignature) {
              // Use times italic for cursive-like appearance
              doc.setFont('times', 'italic');
            } else {
              doc.setFont('helvetica', 'normal');
            }
            
            doc.text(line, 15 + indent, yPosition);
            yPosition += baseFontSize * 0.5;
          }
          
          // Reset font after paragraph
          doc.setFont('helvetica', 'normal');
          
          // Add spacing after paragraph
          yPosition += this.convertCssSizeToPdf('6px');
          break;
          
        case 'ul':
        case 'ol':
          // List styling
          const listItems = Array.from(element.children).filter(child => child.tagName.toLowerCase() === 'li');
          for (const listItem of listItems) {
            processElement(listItem, indent + 20);
          }
          break;
          
        case 'li':
          // List item styling
          doc.setFontSize(baseFontSize);
          doc.setFont('helvetica', 'normal');
          
          // Add bullet point
          doc.text('• ', 15 + indent, yPosition);
          
          const listItemLines = doc.splitTextToSize(textContent, 150 - indent);
          for (let i = 0; i < listItemLines.length; i++) {
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
            }
            if (i === 0) {
              doc.text(listItemLines[i], 25 + indent, yPosition);
            } else {
              doc.text(listItemLines[i], 25 + indent, yPosition);
            }
            yPosition += baseFontSize * 0.5;
          }
          
          // Add spacing after list item
          yPosition += this.convertCssSizeToPdf('2px');
          break;
          
        case 'div':
        case 'span':
          // Process child elements
          for (const child of element.children) {
            processElement(child, indent);
          }
          break;
          
        default:
          // Handle text nodes and other elements
          if (textContent.trim()) {
            // Check if this text contains the signatures section marker
            if (!signaturesPageAdded && (
              textContent.includes('Builders Breakthrough Advisors By:') ||
              textContent.includes('IN WITNESS WHEREOF') ||
              (textContent.includes('Builders Breakthrough Advisors') && textContent.includes('By:'))
            )) {
              // Add page break before signatures section
              doc.addPage();
              yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
              
              // Add "Signatures Page" header
              const headerFontSize = this.convertCssSizeToPdf('24px');
              doc.setFontSize(headerFontSize);
              doc.setFont('helvetica', 'bold');
              doc.text('Signatures Page', 15, yPosition);
              yPosition += headerFontSize * 0.8;
              yPosition += this.convertCssSizeToPdf('16px'); // Extra spacing after header
              
              signaturesPageAdded = true;
            }
            
            doc.setFontSize(baseFontSize);
            
            // Check if this line contains a signature (BBA or client signature)
            const isSignatureLine = /Signature:\s*[^\n]+/i.test(textContent);
            
            if (isSignatureLine) {
              // Use times italic for cursive-like appearance
              doc.setFont('times', 'italic');
            } else {
              doc.setFont('helvetica', 'normal');
            }
            
            // Reduce width slightly to ensure text doesn't run off, especially with special characters
            const lines = doc.splitTextToSize(textContent, 165 - indent);
            for (const line of lines) {
              if (yPosition > 270) {
                doc.addPage();
                yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
              }
              
              // Check if this specific line contains signature text
              const lineIsSignature = /Signature:\s*[^\n]+/i.test(line);
              if (lineIsSignature && !isSignatureLine) {
                doc.setFont('times', 'italic');
              } else if (!lineIsSignature && isSignatureLine) {
                doc.setFont('helvetica', 'normal');
              }
              
              doc.text(line, 15 + indent, yPosition);
              yPosition += baseFontSize * 0.5;
            }
            
            // Reset font after processing
            doc.setFont('helvetica', 'normal');
          }
          
          // Process child elements
          for (const child of element.children) {
            processElement(child, indent);
          }
          break;
      }
    };
    
    // Process all child elements
    for (const child of tempDiv.children) {
      processElement(child);
    }
  }

  static addSimpleDocumentContent(doc, documentContent) {
    // Set up PDF styling
    const baseFontSize = this.convertCssSizeToPdf('14px');
    doc.setFontSize(baseFontSize);
    doc.setFont('helvetica', 'normal');
    
    // Split content into lines (reduce width to ensure text doesn't run off)
    const lines = doc.splitTextToSize(documentContent, 165);
    let yPosition = 25; // Start below logo
    
    // Add document content to PDF with better formatting
    for (let i = 0; i < lines.length; i++) {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
      }
      
      const line = lines[i];
      
      // Check if this line looks like a header (all caps, short line)
      const isHeader = line.length < 50 && line === line.toUpperCase() && line.trim().length > 0;
      
      // Check if this line looks like a section header (ends with colon)
      const isSectionHeader = line.endsWith(':') && line.length < 80 && line.trim().length > 0;
      
      if (isHeader) {
        // Apply main header styling
        const headerFontSize = this.convertCssSizeToPdf('24px');
        doc.setFontSize(headerFontSize);
        doc.setFont('helvetica', 'bold');
        
        // Add spacing before header
        yPosition += this.convertCssSizeToPdf('12px');
        
        doc.text(line, 15, yPosition);
        yPosition += headerFontSize * 0.6;
        
        // Add spacing after header
        yPosition += this.convertCssSizeToPdf('12px');
        
        // Reset to normal text
        doc.setFontSize(baseFontSize);
        doc.setFont('helvetica', 'normal');
      } else if (isSectionHeader) {
        // Apply section header styling
        const sectionFontSize = this.convertCssSizeToPdf('18px');
        doc.setFontSize(sectionFontSize);
        doc.setFont('helvetica', 'bold');
        
        // Add spacing before section header
        yPosition += this.convertCssSizeToPdf('8px');
        
        doc.text(line, 15, yPosition);
        yPosition += sectionFontSize * 0.6;
        
        // Add spacing after section header
        yPosition += this.convertCssSizeToPdf('8px');
        
        // Reset to normal text
        doc.setFontSize(baseFontSize);
        doc.setFont('helvetica', 'normal');
      } else if (line.trim() === '') {
        // Empty line - add spacing
        yPosition += baseFontSize * 0.3;
      } else {
        // Regular text
        doc.text(line, 15, yPosition);
        yPosition += baseFontSize * 0.5; // Better line height
      }
    }
  }

  static stripHtmlTags(html) {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitizeHtml(html);
    
    // Extract text content and clean up
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up extra whitespace and line breaks
    text = text.replace(/\s+/g, ' ').trim();
    
    // Convert HTML entities back to characters
    text = text.replace(/&amp;/g, '&')
               .replace(/&lt;/g, '<')
               .replace(/&gt;/g, '>')
               .replace(/&quot;/g, '"')
               .replace(/&#39;/g, "'")
               .replace(/&nbsp;/g, ' ');
    
    return text;
  }

  static convertCssSizeToPdf(cssSize) {
    if (!cssSize) return 10; // Default font size
    
    // Extract numeric value and unit
    const match = cssSize.toString().match(/^([\d.]+)(px|pt|em|rem)?$/);
    if (!match) return 10;
    
    const value = parseFloat(match[1]);
    const unit = match[2] || 'px';
    
    // Convert to PDF points (1px ≈ 0.75pt, 1pt = 1pt, 1em ≈ 12pt, 1rem ≈ 12pt)
    switch (unit) {
      case 'px':
        return value * 0.75;
      case 'pt':
        return value;
      case 'em':
      case 'rem':
        return value * 12;
      default:
        return value * 0.75; // Default to px conversion
    }
  }

  static estimateElementHeight(element) {
    if (!element.content) return 10;
    
    // Estimate based on element type and content length
    const fontSize = this.convertCssSizeToPdf(element.style?.fontSize || '14px');
    const marginBottom = this.convertCssSizeToPdf(element.style?.marginBottom || '12px');
    
    // Rough estimation: content length / characters per line * line height
    const charsPerLine = 60; // Approximate characters per line
    const contentLength = element.content.length;
    const estimatedLines = Math.ceil(contentLength / charsPerLine);
    const lineHeight = fontSize * 0.4;
    
    return (estimatedLines * lineHeight) + marginBottom;
  }

  static renderStyledElement(doc, element, ndaDocument, yPosition, hasPageBreak = false) {
    if (!element.content) return { yPosition };
    
    let content = element.content;
    
    // Replace variables with actual values
    content = this.replaceVariablesInContent(content, ndaDocument);
    
    // Apply element-specific styling
    switch (element.type) {
      case 'header':
        return this.renderHeaderElement(doc, content, element, ndaDocument, yPosition, hasPageBreak);
      case 'text':
        return this.renderTextElement(doc, content, element, ndaDocument, yPosition, hasPageBreak);
      case 'line':
        return this.renderLineElement(doc, element, yPosition, hasPageBreak);
      case 'signature':
      case 'initial':
      case 'text_input':
      case 'checkbox':
      case 'date':
        return this.renderInputElement(doc, content, element, yPosition, hasPageBreak);
      default:
        return this.renderTextElement(doc, content, element, ndaDocument, yPosition, hasPageBreak);
    }
  }

  static renderHeaderElement(doc, content, element, ndaDocument, yPosition, hasPageBreak = false) {
    // Check if we need a new page (only if not already handled by template page break)
    if (yPosition > 250 && !hasPageBreak) {
      doc.addPage();
      yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
    }
    
    // Add extra spacing before header for better section separation
    const marginTop = this.convertCssSizeToPdf(element.style?.marginTop || '16px');
    yPosition += marginTop;
    
    // Convert CSS font size to PDF points (1px ≈ 0.75pt)
    const fontSize = this.convertCssSizeToPdf(element.style?.fontSize || '24px');
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'bold');
    
    // Split content into lines
    const lines = doc.splitTextToSize(content, 180);
    
    for (const line of lines) {
      if (yPosition > 270 && !hasPageBreak) {
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
      }
      doc.text(line, 15, yPosition);
      yPosition += fontSize * 0.6; // Better spacing for headers
    }
    
    // Add extra spacing after header (convert CSS margin to PDF)
    const marginBottom = this.convertCssSizeToPdf(element.style?.marginBottom || '16px');
    yPosition += marginBottom;
    
    return { yPosition };
  }

  static renderTextElement(doc, content, element, ndaDocument, yPosition, hasPageBreak = false) {
    // Check if we need a new page (only if not already handled by template page break)
    if (yPosition > 250 && !hasPageBreak) {
      doc.addPage();
      yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
    }
    
    // Additional safety check: ensure & before "I authorize" is replaced with checkbox
    // This handles cases where the replacement might have been missed
    if (content.includes('I authorize Builders Breakthrough Advisors') && 
        (content.includes('debit') || content.includes('account'))) {
      content = content.replace(/&[\s\u00A0]*I\s+authorize/i, '☑ I authorize');
    }
    
    // Convert CSS font size to PDF points
    const fontSize = this.convertCssSizeToPdf(element.style?.fontSize || '14px');
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'normal');
    
    // Final safety check: ensure & before "I authorize" is replaced right before rendering
    // This is the last chance to fix it before jsPDF processes it
    if (content && typeof content === 'string') {
      if (content.includes('I authorize') && content.includes('Builders Breakthrough Advisors')) {
        // Replace & (with any whitespace) before "I authorize" - most aggressive pattern
        content = content.replace(/&(amp;)?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, '☑ I authorize');
        // Also handle at start of line
        content = content.replace(/(^|[\r\n\s]+)&+(amp;)?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, '$1☑ I authorize');
      }
    }
    
    // Check if this is authorization text that needs a checkbox
    const isAuthorizationText = content && typeof content === 'string' && 
                                content.includes('I authorize') && 
                                content.includes('Builders Breakthrough Advisors') &&
                                (content.includes('debit') || content.includes('account'));
    
    // Remove & and checkbox symbols from content - we'll draw the checkbox manually
    if (isAuthorizationText) {
      // Remove &, &amp;, checkbox symbols, etc. before "I authorize"
      content = content.replace(/&(amp;)?[\s\u00A0\u2000-\u200B]*(☑|✓|\[X\])?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, 'I authorize');
      content = content.replace(/(^|[\r\n\s]+)(&|☑|✓|\[X\])+[\s\u00A0\u2000-\u200B]*I\s+authorize/i, '$1I authorize');
    }
    
    // Split content into lines (reduce width significantly to ensure text doesn't run off)
    // For authorization text, use even narrower width to prevent overflow (accounting for checkbox)
    const textWidth = isAuthorizationText ? 140 : 165;
    const lines = doc.splitTextToSize(content, textWidth);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (yPosition > 270 && !hasPageBreak) {
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
      }
      
      // If this is the first line of authorization text, draw a checkbox before it
      if (isAuthorizationText && i === 0 && line.trim().toLowerCase().startsWith('i authorize')) {
        // Draw checkbox box - make it smaller and align with text baseline
        const checkboxSize = fontSize * 0.35;
        const checkboxX = 15;
        // Align checkbox with text baseline - yPosition is the baseline, so position checkbox above it
        const checkboxY = yPosition - checkboxSize * 0.85;
        
        // Draw checkbox box
        doc.setLineWidth(0.3);
        doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize);
        
        // Draw checkmark inside the box
        doc.setLineWidth(0.7);
        doc.line(
          checkboxX + checkboxSize * 0.2, 
          checkboxY + checkboxSize * 0.5,
          checkboxX + checkboxSize * 0.4, 
          checkboxY + checkboxSize * 0.75
        );
        doc.line(
          checkboxX + checkboxSize * 0.4, 
          checkboxY + checkboxSize * 0.75,
          checkboxX + checkboxSize * 0.8, 
          checkboxY + checkboxSize * 0.25
        );
        
        // Render text to the right of the checkbox with smaller spacing
        doc.text(line, checkboxX + checkboxSize + 2, yPosition);
      } else {
        // Regular text rendering
        doc.text(line, 15, yPosition);
      }
      
      yPosition += fontSize * 0.5; // Better line height for readability
    }
    
    // Render sub-items if they exist
    if (element.subItems && element.subItems.length > 0) {
      yPosition = this.renderSubItems(doc, element.subItems, ndaDocument, yPosition, hasPageBreak);
    }
    
    // Add spacing after text element (convert CSS margin to PDF)
    const marginBottom = this.convertCssSizeToPdf(element.style?.marginBottom || '12px');
    yPosition += marginBottom;
    
    return { yPosition };
  }

  static renderSubItems(doc, subItems, ndaDocument, yPosition, hasPageBreak = false) {
    // Use the same font size as main content (14px)
    const textFontSize = this.convertCssSizeToPdf('14px');
    
    for (const subItem of subItems) {
      if (yPosition > 270 && !hasPageBreak) {
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
      }
      
      // Add spacing before sub-item
      yPosition += 2;
      
      // Render sub-item content
      let content = subItem.text || subItem.content || 'Empty sub-item';
      content = this.replaceVariablesInContent(content, ndaDocument);
      
      // Check if this is authorization text that needs a checkbox
      const isAuthorizationText = content && typeof content === 'string' && 
                                  content.includes('I authorize') && 
                                  content.includes('Builders Breakthrough Advisors') &&
                                  (content.includes('debit') || content.includes('account'));
      
      // Remove & and checkbox symbols from content - we'll draw the checkbox manually
      if (isAuthorizationText) {
        content = content.replace(/&(amp;)?[\s\u00A0\u2000-\u200B]*(☑|✓|\[X\])?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, 'I authorize');
        content = content.replace(/(^|[\r\n\s]+)(&|☑|✓|\[X\])+[\s\u00A0\u2000-\u200B]*I\s+authorize/i, '$1I authorize');
      }
      
      // Add bullet point with same font size as content (unless it's authorization text - we'll use checkbox instead)
      doc.setFontSize(textFontSize);
      doc.setFont('helvetica', 'normal');
      
      if (!isAuthorizationText) {
        doc.text('• ', 20, yPosition);
      }
      
      // Render sub-item text with same font size as main content
      doc.setFontSize(textFontSize);
      const textWidth = isAuthorizationText ? 145 : 150;
      const lines = doc.splitTextToSize(content, textWidth);
      for (let i = 0; i < lines.length; i++) {
        if (yPosition > 270 && !hasPageBreak) {
          doc.addPage();
          yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
        }
        
        const line = lines[i];
        // If this is the first line of authorization text, draw a checkbox before it
        if (isAuthorizationText && i === 0 && line.trim().toLowerCase().startsWith('i authorize')) {
          // Draw checkbox box - make it smaller and align with text baseline
          const checkboxSize = textFontSize * 0.35;
          const checkboxX = 20;
          // Align checkbox with text baseline - yPosition is the baseline, so position checkbox above it
          const checkboxY = yPosition - checkboxSize * 0.85;
          
          // Draw checkbox box
          doc.setLineWidth(0.3);
          doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize);
          
          // Draw checkmark inside the box
          doc.setLineWidth(0.7);
          doc.line(
            checkboxX + checkboxSize * 0.2, 
            checkboxY + checkboxSize * 0.5,
            checkboxX + checkboxSize * 0.4, 
            checkboxY + checkboxSize * 0.75
          );
          doc.line(
            checkboxX + checkboxSize * 0.4, 
            checkboxY + checkboxSize * 0.75,
            checkboxX + checkboxSize * 0.8, 
            checkboxY + checkboxSize * 0.25
          );
          
          // Render text to the right of the checkbox with smaller spacing
          doc.text(line, checkboxX + checkboxSize + 2, yPosition);
        } else {
          // Regular text rendering
          doc.text(line, 25, yPosition);
        }
        
        yPosition += textFontSize * 0.5; // Better line height
      }
      
      // Render nested sub-items if they exist
      if (subItem.subItems && subItem.subItems.length > 0) {
        yPosition = this.renderNestedSubItems(doc, subItem.subItems, ndaDocument, yPosition, hasPageBreak);
      }
      
      yPosition += 4; // Better spacing between sub-items
    }
    
    return yPosition;
  }

  static renderNestedSubItems(doc, nestedSubItems, ndaDocument, yPosition, hasPageBreak = false) {
    // Use the same font size as main content (14px)
    const textFontSize = this.convertCssSizeToPdf('14px');
    
    for (const nestedItem of nestedSubItems) {
      if (yPosition > 270 && !hasPageBreak) {
        doc.addPage();
        yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
      }
      
      // Add spacing before nested sub-item
      yPosition += 1;
      
      // Render nested sub-item content
      let content = nestedItem.text || nestedItem.content || 'Empty nested sub-item';
      content = this.replaceVariablesInContent(content, ndaDocument);
      
      // Check if this is authorization text that needs a checkbox
      const isAuthorizationText = content && typeof content === 'string' && 
                                  content.includes('I authorize') && 
                                  content.includes('Builders Breakthrough Advisors') &&
                                  (content.includes('debit') || content.includes('account'));
      
      // Remove & and checkbox symbols from content - we'll draw the checkbox manually
      if (isAuthorizationText) {
        content = content.replace(/&(amp;)?[\s\u00A0\u2000-\u200B]*(☑|✓|\[X\])?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, 'I authorize');
        content = content.replace(/(^|[\r\n\s]+)(&|☑|✓|\[X\])+[\s\u00A0\u2000-\u200B]*I\s+authorize/i, '$1I authorize');
      }
      
      // Add nested bullet point with same font size as content (unless it's authorization text - we'll use checkbox instead)
      doc.setFontSize(textFontSize);
      doc.setFont('helvetica', 'normal');
      
      if (!isAuthorizationText) {
        doc.text('  • ', 30, yPosition);
      }
      
      // Render nested sub-item text with same font size as main content
      doc.setFontSize(textFontSize);
      const textWidth = isAuthorizationText ? 135 : 140;
      const lines = doc.splitTextToSize(content, textWidth);
      for (let i = 0; i < lines.length; i++) {
        if (yPosition > 270 && !hasPageBreak) {
          doc.addPage();
          yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
        }
        
        const line = lines[i];
        // If this is the first line of authorization text, draw a checkbox before it
        if (isAuthorizationText && i === 0 && line.trim().toLowerCase().startsWith('i authorize')) {
          // Draw checkbox box - make it smaller and align with text baseline
          const checkboxSize = textFontSize * 0.35;
          const checkboxX = 30;
          // Align checkbox with text baseline - yPosition is the baseline, so position checkbox above it
          const checkboxY = yPosition - checkboxSize * 0.85;
          
          // Draw checkbox box
          doc.setLineWidth(0.3);
          doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize);
          
          // Draw checkmark inside the box
          doc.setLineWidth(0.7);
          doc.line(
            checkboxX + checkboxSize * 0.2, 
            checkboxY + checkboxSize * 0.5,
            checkboxX + checkboxSize * 0.4, 
            checkboxY + checkboxSize * 0.75
          );
          doc.line(
            checkboxX + checkboxSize * 0.4, 
            checkboxY + checkboxSize * 0.75,
            checkboxX + checkboxSize * 0.8, 
            checkboxY + checkboxSize * 0.25
          );
          
          // Render text to the right of the checkbox with smaller spacing
          doc.text(line, checkboxX + checkboxSize + 2, yPosition);
        } else {
          // Regular text rendering
          doc.text(line, 35, yPosition);
        }
        
        yPosition += textFontSize * 0.5; // Better line height
      }
      
      yPosition += 3; // Better spacing between nested items
    }
    
    return yPosition;
  }

  static renderLineElement(doc, element, yPosition, hasPageBreak = false) {
    // Check if we need a new page (only if not already handled by template page break)
    if (yPosition > 270 && !hasPageBreak) {
      doc.addPage();
      yPosition = 25; // Start below logo on new page
    }
    
    // Convert CSS margin to PDF spacing
    const marginTop = this.convertCssSizeToPdf(element.style?.marginTop || '16px');
    const marginBottom = this.convertCssSizeToPdf(element.style?.marginBottom || '16px');
    
    // Add top margin
    yPosition += marginTop;
    
    // Draw a line with proper thickness
    const lineWidth = this.convertCssSizeToPdf(element.style?.borderBottom || '1px solid #ccc');
    doc.setLineWidth(lineWidth);
    doc.line(15, yPosition, 195, yPosition);
    
    // Add bottom margin
    yPosition += marginBottom;
    
    return { yPosition };
  }

  static renderInputElement(doc, content, element, yPosition, hasPageBreak = false) {
    // Check if we need a new page (only if not already handled by template page break)
    if (yPosition > 270 && !hasPageBreak) {
      doc.addPage();
      yPosition = 25; // Start below logo on new page
    }
    
    // Fix content: replace & before "I authorize" with checkbox symbol
    // This handles cases where the content has "&" instead of checkbox
    if (content && typeof content === 'string') {
      if (content.includes('I authorize Builders Breakthrough Advisors') && 
          (content.includes('debit') || content.includes('account'))) {
        // Replace & (with any whitespace) before "I authorize" with checkbox
        content = content.replace(/&[\s\u00A0]*I\s+authorize/i, '☑ I authorize');
        // Also handle HTML entities
        content = content.replace(/&amp;[\s\u00A0]*I\s+authorize/i, '☑ I authorize');
        content = content.replace(/&nbsp;[\s\u00A0]*I\s+authorize/i, '☑ I authorize');
      }
    }
    
    // Convert CSS font size to PDF points
    const fontSize = this.convertCssSizeToPdf(element.style?.fontSize || '14px');
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'normal');
    
    // For signature and initial fields, add underline and use cursive font
    if (element.type === 'signature' || element.type === 'initial') {
      // Use times font with italic for cursive-like appearance
      // Note: jsPDF doesn't have a true cursive font, but times italic looks more script-like
      doc.setFont('times', 'italic');
      const lines = doc.splitTextToSize(content, 165);
      for (const line of lines) {
        if (yPosition > 270 && !hasPageBreak) {
          doc.addPage();
          yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
        }
        doc.text(line, 15, yPosition);
        // Draw underline with proper thickness
        const textWidth = doc.getTextWidth(line);
        const lineWidth = this.convertCssSizeToPdf(element.style?.borderBottom || '1px solid #000');
        doc.setLineWidth(lineWidth);
        doc.line(15, yPosition + 2, 15 + textWidth, yPosition + 2);
        yPosition += fontSize * 0.6; // Proportional spacing
      }
      // Reset font back to normal after signature
      doc.setFont('helvetica', 'normal');
    } else if (element.type === 'checkbox') {
      // For checkbox elements, ensure checkbox symbol is present
      let checkboxContent = content || element.label || '';
      
      // If content doesn't start with checkbox and contains "I authorize", fix it
      if (checkboxContent.includes('I authorize Builders Breakthrough Advisors') && 
          (checkboxContent.includes('debit') || checkboxContent.includes('account'))) {
        // Remove any leading & or checkbox symbols and add proper checkbox
        checkboxContent = checkboxContent.replace(/^[\s\u00A0]*(&|☑|✓|\[X\])[\s\u00A0]*/i, '');
        checkboxContent = '☑ ' + checkboxContent;
      } else if (!checkboxContent.startsWith('☑') && !checkboxContent.startsWith('✓') && !checkboxContent.startsWith('[X]')) {
        // Add checkbox symbol if not present
        checkboxContent = '☑ ' + checkboxContent;
      }
      
      // Draw a checkbox box with checkmark using PDF drawing commands
      // This ensures it renders correctly even if Unicode doesn't work
      const checkboxSize = fontSize * 0.6;
      const checkboxX = 15;
      const checkboxY = yPosition - checkboxSize * 0.3;
      
      // Draw checkbox box
      doc.setLineWidth(0.5);
      doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize);
      
      // Draw checkmark inside the box
      doc.setLineWidth(1);
      doc.line(
        checkboxX + checkboxSize * 0.2, 
        checkboxY + checkboxSize * 0.5,
        checkboxX + checkboxSize * 0.4, 
        checkboxY + checkboxSize * 0.75
      );
      doc.line(
        checkboxX + checkboxSize * 0.4, 
        checkboxY + checkboxSize * 0.75,
        checkboxX + checkboxSize * 0.8, 
        checkboxY + checkboxSize * 0.25
      );
      
      // Remove checkbox symbol from text since we're drawing it
      const textContent = checkboxContent.replace(/^[\s\u00A0]*(☑|✓|\[X\])[\s\u00A0]*/, '').trim();
      
      const lines = doc.splitTextToSize(textContent, 150); // Reduced width to account for checkbox
      for (let i = 0; i < lines.length; i++) {
        if (yPosition > 270 && !hasPageBreak) {
          doc.addPage();
          yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
          // Redraw checkbox on new page if needed
          if (i === 0) {
            const newCheckboxY = yPosition - checkboxSize * 0.3;
            doc.setLineWidth(0.5);
            doc.rect(checkboxX, newCheckboxY, checkboxSize, checkboxSize);
            doc.setLineWidth(1);
            doc.line(
              checkboxX + checkboxSize * 0.2, 
              newCheckboxY + checkboxSize * 0.5,
              checkboxX + checkboxSize * 0.4, 
              newCheckboxY + checkboxSize * 0.75
            );
            doc.line(
              checkboxX + checkboxSize * 0.4, 
              newCheckboxY + checkboxSize * 0.75,
              checkboxX + checkboxSize * 0.8, 
              newCheckboxY + checkboxSize * 0.25
            );
          }
        }
        // Position text to the right of the checkbox
        doc.text(lines[i], checkboxX + checkboxSize + 3, yPosition);
        yPosition += fontSize * 0.5; // Better line height
      }
    } else {
      // Regular input field rendering - also fix authorization text here
      let processedContent = this.fixAuthorizationCheckbox(content);
      
      const lines = doc.splitTextToSize(processedContent, 165);
      for (const line of lines) {
        if (yPosition > 270 && !hasPageBreak) {
          doc.addPage();
          yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
        }
        doc.text(line, 15, yPosition);
        yPosition += fontSize * 0.5; // Better line height
      }
    }
    
    // Add spacing after input element (convert CSS margin to PDF)
    const marginBottom = this.convertCssSizeToPdf(element.style?.marginBottom || '8px');
    yPosition += marginBottom;
    
    return { yPosition };
  }

  static renderElementContent(element, ndaDocument) {
    if (!element.content) return '';
    
    let content = element.content;
    
    // Replace variables with actual values
    content = this.replaceVariablesInContent(content, ndaDocument);
    
    // Add formatting based on element type
    if (element.type === 'header') {
      return `\n${content}\n`;
    } else if (element.type === 'line') {
      return '\n' + '─'.repeat(50) + '\n';
    }
    
    return content;
  }

  static replaceVariablesInContent(content, ndaDocument) {
    if (!content) return '';
    
    // Get client data from leadData first, then fall back to ndaDocument
    const clientData = ndaDocument.leadData || {};
    const bbaData = ndaDocument.bbaFormData || {};
    
    // Replace common variables with priority: leadData > ndaDocument > placeholder
    const replacements = {
      '[Company Name]': clientData.companyName || ndaDocument.companyName || '[Company Name]',
      '[Company_Name]': clientData.companyName || ndaDocument.companyName || '[Company_Name]',
      '[State]': clientData.state || ndaDocument.state || '[State]',
      '[Company Address]': clientData.companyAddress || ndaDocument.companyAddress || '[Company Address]',
      '[Company_Address]': clientData.companyAddress || ndaDocument.companyAddress || '[Company_Address]',
      '[City]': clientData.city || ndaDocument.city || '[City]',
      '[Zip Code]': clientData.zipCode || ndaDocument.zipCode || '[Zip Code]',
      '[Zip_Code]': clientData.zipCode || ndaDocument.zipCode || '[Zip_Code]',
      '[Business Structure]': clientData.businessStructure || ndaDocument.businessStructure || '[Business Structure]',
      '[Business_Structure]': clientData.businessStructure || ndaDocument.businessStructure || '[Business_Structure]',
      '[Signer Name]': clientData.signerName || ndaDocument.signerName || '[Signer Name]',
      '[Signer_Name]': clientData.signerName || ndaDocument.signerName || '[Signer_Name]',
      '[Signer Title]': clientData.signerTitle || ndaDocument.signerTitle || '[Signer Title]',
      '[Signer_Title]': clientData.signerTitle || ndaDocument.signerTitle || '[Signer_Title]',
      '[Owner Name]': clientData.signerName || ndaDocument.signerName || '[Owner Name]',
      '[Owner Title]': clientData.signerTitle || ndaDocument.signerTitle || '[Owner Title]',
      '[SIGNATURE]': ndaDocument.signature || '[SIGNATURE]',
      '[INITIAL]': ndaDocument.initial || '[INITIAL]',
      '[INITIALS]': ndaDocument.initial || '[INITIALS]',
      '[Date]': new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
    
    // Replace BBA signature section variables
    const bbaReplacements = {
      '[Name]': bbaData['default-bba-name'] || '[Name]',
      '[Title]': bbaData['default-bba-title'] || '[Title]',
      '[Signature]': bbaData['default-bba-signature'] || '[Signature]',
      '[Date]': bbaData['default-bba-date'] || new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
    
    // Combine all replacements
    const allReplacements = { ...replacements, ...bbaReplacements };
    
    let updatedContent = content;
    for (const [variable, value] of Object.entries(allReplacements)) {
      updatedContent = updatedContent.replace(new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
    }
    
    // Handle [CHECKBOX] variable
    updatedContent = updatedContent.replace(/\[CHECKBOX\]/gi, '☑ ');
    
    // Fix authorization text: replace & characters with checkbox
    // First, handle HTML entities that might have been encoded
    updatedContent = updatedContent.replace(/&amp;/g, '&');
    updatedContent = updatedContent.replace(/&nbsp;/g, ' ');
    
      // Always check for authorization text pattern and fix it
      // This is the most common issue - "&  I authorize" should be "☑ I authorize"
      if (updatedContent.includes('I authorize') && updatedContent.includes('Builders Breakthrough Advisors')) {
        // Most aggressive pattern: match & (with any amount of whitespace) followed by "I authorize"
        // This handles: "&  I authorize", "& I authorize", "&amp;  I authorize", etc.
        const checkboxSymbol = this.getCheckboxSymbol();
        updatedContent = updatedContent.replace(/&(amp;)?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, checkboxSymbol + ' I authorize');
        
        // Handle multiple ampersands
        updatedContent = updatedContent.replace(/&+(amp;)?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, checkboxSymbol + ' I authorize');
        
        // Handle at start of string or after whitespace
        updatedContent = updatedContent.replace(/(^|[\s\u00A0\u2000-\u200B\r\n]+)&+(amp;)?[\s\u00A0\u2000-\u200B]*I\s+authorize/i, '$1' + checkboxSymbol + ' I authorize');
        
        // Ensure checkbox is present - if no checkbox found but "I authorize" exists, add it
        if (!updatedContent.includes('☑') && !updatedContent.includes('✓') && updatedContent.match(/I\s+authorize/i)) {
          updatedContent = updatedContent.replace(/(^|\s)(I\s+authorize)/i, '$1' + checkboxSymbol + ' $2');
        }
        
        // Fix any remaining standalone & characters in the middle of the text (replace with "and")
        updatedContent = updatedContent.replace(/\s+&+(amp;)?\s+/g, ' and ');
        // Also fix & at the start of words
        updatedContent = updatedContent.replace(/\s+&+(amp;)?([a-zA-Z])/g, ' and $2');
      }
    
    return updatedContent;
  }

  static addAuditDetailsPage(doc, ndaDocument) {
    // Add new page for audit details
    doc.addPage();
    
    // Set up styling for audit page
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('AUDIT DETAILS', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('E-SIGN ACT COMPLIANCE RECORD', 105, 40, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Start with E-SIGN Act Compliance section (skip redundant company/signature info)
    let yPosition = 60;
    doc.setFont('helvetica', 'bold');
    doc.text('E-SIGN ACT COMPLIANCE', 15, yPosition);
    doc.setFont('helvetica', 'normal');
    yPosition += 10;
    
    const complianceText = [
      'This document has been electronically signed in compliance with the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).',
      '',
      'COMPLIANCE VERIFICATION:',
      '✓ Electronic signature consent recorded',
      '✓ Electronic records consent recorded', 
      '✓ Audit trail created',
      '✓ Tamper-proof document snapshot stored',
      '',
      'SIGNATURE DETAILS:',
      `Signer: ${ndaDocument.signature || ndaDocument.signerName || 'N/A'}`,
      `Title: ${ndaDocument.signerTitle || 'N/A'}`,
      `Company: ${ndaDocument.companyName || 'N/A'}`,
      `Date: ${ndaDocument.signedAt ? new Date(ndaDocument.signedAt).toLocaleString() : new Date().toLocaleString()}`,
      `Electronic Signature: ${ndaDocument.signature || 'N/A'}`,
      '',
      'DOCUMENT INTEGRITY:',
      'This document includes cryptographic hash verification to ensure integrity and prevent tampering.'
    ];
    
    for (const line of complianceText) {
      if (line.trim() === '') {
        // Empty line - add spacing
        yPosition += 6;
      } else {
        // Check if this is a compliance verification line (starts with checkmark)
        const isComplianceItem = line.trim().startsWith('✓') || line.trim().startsWith('☑');
        
        if (isComplianceItem) {
          // Remove checkmark character from text
          const textContent = line.replace(/^[\s\u00A0]*(✓|☑|&)[\s\u00A0]*/, '').trim();
          
          // Draw checkbox
          const fontSize = 10; // Current font size
          const checkboxSize = fontSize * 0.35;
          const checkboxX = 15;
          const checkboxY = yPosition - checkboxSize * 0.85;
          
          // Draw checkbox box
          doc.setLineWidth(0.3);
          doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize);
          
          // Draw checkmark inside the box
          doc.setLineWidth(0.7);
          doc.line(
            checkboxX + checkboxSize * 0.2, 
            checkboxY + checkboxSize * 0.5,
            checkboxX + checkboxSize * 0.4, 
            checkboxY + checkboxSize * 0.75
          );
          doc.line(
            checkboxX + checkboxSize * 0.4, 
            checkboxY + checkboxSize * 0.75,
            checkboxX + checkboxSize * 0.8, 
            checkboxY + checkboxSize * 0.25
          );
          
          // Split text to fit within page width (accounting for checkbox)
          const wrappedLines = doc.splitTextToSize(textContent, 170);
          for (let i = 0; i < wrappedLines.length; i++) {
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 40;
              // Redraw checkbox on new page if needed
              if (i === 0) {
                const newCheckboxY = yPosition - checkboxSize * 0.85;
                doc.setLineWidth(0.3);
                doc.rect(checkboxX, newCheckboxY, checkboxSize, checkboxSize);
                doc.setLineWidth(0.7);
                doc.line(
                  checkboxX + checkboxSize * 0.2, 
                  newCheckboxY + checkboxSize * 0.5,
                  checkboxX + checkboxSize * 0.4, 
                  newCheckboxY + checkboxSize * 0.75
                );
                doc.line(
                  checkboxX + checkboxSize * 0.4, 
                  newCheckboxY + checkboxSize * 0.75,
                  checkboxX + checkboxSize * 0.8, 
                  newCheckboxY + checkboxSize * 0.25
                );
              }
            }
            // Render text to the right of the checkbox
            doc.text(wrappedLines[i], checkboxX + checkboxSize + 2, yPosition);
            yPosition += 6;
          }
        } else {
          // Regular text - split long lines to fit within page width
          const wrappedLines = doc.splitTextToSize(line, 180);
          for (const wrappedLine of wrappedLines) {
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 40; // Start below logo on new page (logo 20mm tall + 10mm spacing)
            }
            doc.text(wrappedLine, 15, yPosition);
            yPosition += 6;
          }
        }
      }
    }
    
    // Add hash verification
    yPosition += 10;
    const hash = CryptoJS.SHA256(ndaDocument.documentContent || this.generateFullNDAContent(ndaDocument)).toString();
    doc.text(`Document Hash: ${hash}`, 15, yPosition);
    doc.text('This hash verifies document integrity and prevents tampering.', 15, yPosition + 10);
  }

  static generateFullNDAContent(ndaDocument) {
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Get client data from leadData first, then fall back to ndaDocument
    const clientData = ndaDocument.leadData || {};
    const bbaData = ndaDocument.bbaFormData || {};
    
    // Extract company information with priority: leadData > ndaDocument > placeholder
    let companyName = clientData.companyName || ndaDocument.companyName || '[Company Name]';
    let businessStructure = clientData.businessStructure || ndaDocument.businessStructure || '[Business Structure]';
    let state = clientData.state || ndaDocument.state || '[State]';
    let companyAddress = clientData.companyAddress || ndaDocument.companyAddress || '[Company Address]';
    let city = clientData.city || ndaDocument.city || '[City]';
    let zipCode = clientData.zipCode || ndaDocument.zipCode || '[Zip Code]';
    
    // For backward compatibility: if we have a combined address but no separate fields,
    // try to extract information from the combined address
    if (!city && !state && !zipCode && companyAddress && companyAddress !== '[Company Address]') {
      const addressParts = companyAddress.split(', ');
      if (addressParts.length >= 3) {
        companyAddress = addressParts[0] || '[Company Address]';
        city = addressParts[1] || '[City]';
        const stateZip = addressParts[2] || '';
        const stateZipParts = stateZip.split(' ');
        if (stateZipParts.length >= 2) {
          state = stateZipParts[0] || '[State]';
          zipCode = stateZipParts[1] || '[Zip Code]';
        }
      }
    }
    
    const signerName = clientData.signerName || ndaDocument.signerName || '[Owner Name]';
    const signerTitle = clientData.signerTitle || ndaDocument.signerTitle || '[Owner Title]';
    const signature = ndaDocument.signature || '[Signature]';
    const initial = ndaDocument.initial || '[Initials]';

    return `Non-Disclosure Agreement

This Non-Disclosure Agreement ("Agreement") is entered into as of ${currentDate} by and between:

Builders Breakthrough Advisors, a limited liability corporation organized and existing under the laws of North Carolina, USA with its principal office located at 5102 Durham Chapel Hill Blvd Durham NC 27707 ("Receiving Party"),

AND

${companyName}, a ${businessStructure} organized and existing under the laws of ${state}, USA with its principal office located at ${companyAddress}, ${city}, ${zipCode} ("Disclosing Party").

WHEREAS,

The Disclosing Party and the Receiving Party wish to establish a confidential relationship with respect to the disclosure of certain proprietary and confidential information ("Confidential Information").

Initials: ${initial}

NOW, THEREFORE, in consideration of the mutual covenants and conditions herein contained, the parties hereto agree as follows:

1. Definition of Confidential Information
   For purposes of this Agreement, "Confidential Information" includes all written, electronic, or oral information that the Disclosing Party provides to the Receiving Party, including but not limited to business strategies, drawings, designs, plans, proposals, financial data, and client information that the Receiving Party understands is confidential.

2. Obligations of the Receiving Party
   The Receiving Party shall keep the Confidential Information confidential and shall not disclose such information to any third party without the prior written consent of the Disclosing Party.
   The Receiving Party shall use the same degree of care to protect the confidentiality of the Confidential Information as it uses to protect its own confidential information of like kind, but in no event less than reasonable care.

3. Exclusions from Confidential Information
   Confidential Information does not include information that:
   - is or becomes publicly known through no breach of this Agreement by the Receiving Party;
   - is received from a third party without breach of any obligation of confidentiality;
   - is independently developed by the Receiving Party without use of or reference to the Disclosing Party's Confidential Information;
   - is disclosed by Receiving Party with Disclosing Party's prior written approval.

4. Term
   This Agreement shall commence on the date first above written and shall continue in effect until the Confidential Information no longer qualifies as confidential.

5. Assignment
   This Agreement may not be assigned or otherwise transferred by either party without the prior written consent of the non-transferring party.

6. Binding Arrangement
   This Agreement will be binding upon and inure to the benefit of the parties hereto and each Party's respective successors and assigns.

7. Severability
   In the event that any provision of this Agreement is held by a court of competent jurisdiction to be unenforceable because it is invalid or in conflict with any law of any relevant jurisdiction, the validity of the remaining provisions shall not be affected, and the rights and obligations of the parties hereto shall be construed and enforced as if the Agreement did not contain the particular provision(s) held to be unenforceable.

Initials: ${initial}

8. Return of Materials
   All documents and other tangible materials containing or representing Confidential Information and all copies thereof which are in the possession of the Receiving Party shall be and remain the property of the Disclosing Party and shall be promptly returned to the Disclosing Party upon the Disclosing Party's written request.

9. No License
   Nothing in this Agreement is intended to grant any rights to the Receiving Party under any patent, trademark, copyright, or other intellectual property rights of the Disclosing Party.

10. Indemnification
    Recipient agrees to indemnify and keep the Company at all times fully and effectively indemnified in respect of any and all claims, demands, losses, damages, liabilities, costs and or expenses of any kind whatsoever incurred by the Company which arise out of or in connection with any breach of this Agreement by the Recipient.

11. Notice of Disclosure
    In the event that the Recipient receives a request or is required (by deposition, interrogatory, request for documents, subpoena, civil investigative demand or similar process) to disclose all or any part of the Confidential Information, the Recipient agrees, if legally permissible, to:
    - promptly notify the Company of the existence, terms and circumstances surrounding such request or requirement,
    - consult with the Company on the advisability of taking legally available steps to resist or narrow such request or requirement and
    - assist the Company in seeking a protective order or other appropriate remedy; provided, however, that the Recipient shall not be required to take any action in violation of applicable laws. In the event that such protective order or other remedy is not obtained or that the Company waives compliance with the provisions hereof, the Recipient shall not be liable for such disclosure unless disclosure to any such tribunal was caused by or resulted from a previous disclosure by the Recipient not permitted by this Agreement.

12. Miscellaneous
    This Agreement constitutes the entire agreement between the parties concerning the subject matter hereof, replacing any prior agreements. It may only be changed by mutual agreement of the parties in writing.

13. Authority
    Each party hereto represents and warrants that it has the full power and authority to enter into and perform this Agreement, and each party knows of no law, rule, regulations, order, agreement, promise, undertaking or other fact or circumstance which would prevent its full execution and performance of this Agreement.

IN WITNESS WHEREOF, the parties have executed this Non-Disclosure Agreement as of the latest date set forth below.

Initials: ${initial}

Builders Breakthrough Advisors By:

Name: ${bbaData['default-bba-name'] || '[Name]'}

Title: ${bbaData['default-bba-title'] || '[Title]'}

Signature: ${bbaData['default-bba-signature'] || '[Signature]'}

Date: ${bbaData['default-bba-date'] || currentDate}

${companyName} By:

Name: ${signerName}

Title: ${signerTitle}

Signature: ${signature}

Date: ${currentDate}

---
This document has been electronically signed in compliance with the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).`;
  }

  static extractDocumentContent(ndaDocument) {
    // Generate the full document content first to ensure all variables are properly filled
    const fullContent = this.generateFullNDAContent(ndaDocument);
    
    return JSON.stringify({
      title: ndaDocument.title,
      companyName: ndaDocument.companyName,
      companyAddress: ndaDocument.companyAddress,
      city: ndaDocument.city,
      state: ndaDocument.state,
      zipCode: ndaDocument.zipCode,
      businessStructure: ndaDocument.businessStructure,
      signerName: ndaDocument.signerName,
      signerTitle: ndaDocument.signerTitle,
      signature: ndaDocument.signature,
      signedAt: ndaDocument.signedAt,
      eSignCompliance: ndaDocument.eSignCompliance,
      documentSnapshot: ndaDocument.eSignCompliance?.recordIntegrity?.documentSnapshot,
      fullDocumentContent: fullContent
    }, null, 2);
  }

  static async downloadNDAPDF(ndaDocument) {
    try {
      const { pdf, hash, content } = await this.generateNDAPDF(ndaDocument);
      const filename = `NDA_${ndaDocument.signerName || 'signed'}_${hash.substring(0, 8)}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      localStorage.setItem(`nda_hash_${ndaDocument.id}`, hash);
      localStorage.setItem(`nda_content_${ndaDocument.id}`, content);
      return { success: true, filename: filename, hash: hash };
    } catch (error) {
      console.error('Error generating PDF:', error);
      return { success: false, error: error.message };
    }
  }

  static verifyDocumentIntegrity(documentId) {
    const storedHash = localStorage.getItem(`nda_hash_${documentId}`);
    const storedContent = localStorage.getItem(`nda_content_${documentId}`);
    if (!storedHash || !storedContent) {
      return { verified: false, error: 'No stored hash found for this document' };
    }
    const currentHash = CryptoJS.SHA256(storedContent).toString();
    const isVerified = currentHash === storedHash;
    return { verified: isVerified, hash: storedHash, content: storedContent, message: isVerified ? 'Document integrity verified' : 'Document may have been tampered with' };
  }

  static generateNDAPreview(ndaDocument) {
    const hash = CryptoJS.SHA256(this.generateFullNDAContent(ndaDocument)).toString();
    return this.generateFullNDAContent(ndaDocument) + `

---
DOCUMENT INTEGRITY VERIFICATION:
Hash: ${hash}
This hash verifies document integrity and prevents tampering.

This document has been electronically signed in compliance with the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).

IMPORTANT: Download the PDF version for the complete tamper-proof document with full hash verification.`;
  }

  static async addHeaderAndFooter(doc) {
    const totalPages = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Load logo image as base64
    let logoData = null;
    try {
      // Try to load logo from public folder
      const logoPath = window.location.origin + '/logo.png';
      const response = await fetch(logoPath);
      if (response.ok) {
        const blob = await response.blob();
        const reader = new FileReader();
        logoData = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
    } catch (error) {
      console.warn('Could not load logo image:', error);
    }
    
    // Iterate through all pages
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Add logo in top left (10mm from left, 10mm from top)
      if (logoData) {
        try {
          // Add logo image (60mm wide, 20mm tall - twice the original size)
          doc.addImage(logoData, 'PNG', 10, 10, 60, 20);
        } catch (error) {
          console.warn('Could not add logo to page:', error);
        }
      }
      
      // Add page number in bottom right
      const pageNumberText = `Page ${i} of ${totalPages}`;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const textWidth = doc.getTextWidth(pageNumberText);
      doc.text(pageNumberText, pageWidth - textWidth - 10, pageHeight - 10);
    }
  }
} 